import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Camera, Carrot, ChefHat, Calculator, Receipt, LogOut, Settings } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Logo";

const navItems = [
  { to: "/", label: "Home", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/capture", label: "Snap", icon: Camera, testId: "nav-capture" },
  { to: "/ingredients", label: "Ingredients", icon: Carrot, testId: "nav-ingredients" },
  { to: "/recipes", label: "Recipes", icon: ChefHat, testId: "nav-recipes" },
  { to: "/quote", label: "Quote", icon: Calculator, testId: "nav-quote" },
  { to: "/invoices", label: "Invoices", icon: Receipt, testId: "nav-invoices" },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-cream text-ink font-body">
      <header className="sticky top-0 z-40 bg-cream/95 backdrop-blur-sm border-b border-sand pt-[env(safe-area-inset-top)]">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="logo-home-button" className="flex items-center gap-2">
            <Logo markSize={34} textSize="text-2xl" />
          </button>
          <nav className="hidden md:flex items-center gap-1">
            {navItems.map(({ to, label, icon: Icon, testId }) => (
              <NavLink key={to} to={to} end={to === "/"} data-testid={`desktop-${testId}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    isActive ? "bg-tea text-tea-text" : "text-ink/70 hover:bg-sand"}`}>
                <Icon size={16} /> {label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm text-ink/60" data-testid="user-name-display">{user?.name}</span>
            <button onClick={() => navigate("/settings")} data-testid="settings-button"
              className="p-2 rounded-full hover:bg-sand transition-colors duration-200 text-ink/60" title="Settings">
              <Settings size={18} />
            </button>
            <button onClick={logout} data-testid="logout-button"
              className="p-2 rounded-full hover:bg-sand transition-colors duration-200 text-ink/60" title="Log out">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-6 pb-28 md:pb-12">{children}</main>

      <footer className="hidden md:flex max-w-5xl mx-auto px-5 pb-8 items-center gap-4 text-xs text-ink/40">
        <a href="/terms" className="hover:text-ink/60">Terms</a>
        <a href="/privacy" className="hover:text-ink/60">Privacy</a>
        <a href="mailto:hello@mnandi.app" className="hover:text-ink/60">hello@mnandi.app</a>
      </footer>

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-cream border-t border-sand">
        <div className="grid grid-cols-6">
          {navItems.map(({ to, label, icon: Icon, testId }) => (
            <NavLink key={to} to={to} end={to === "/"} data-testid={testId}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-3 text-[11px] font-medium transition-colors duration-200 ${
                  isActive ? "text-tea-text" : "text-ink/50"}`}>
              <Icon size={20} /> {label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};
